import persistentStore from '../persistentStore'
import isEmpty from 'lodash/isEmpty'
import request from '../client/request'

const statusBase = new Map([
  ['Aprobado', 'ACTIVE'],
  ['Deshabilitado', 'ARCHIVED'],
  ['Pendiente aprobación', 'DRAFT']
])
const onlyUnique = (value, index, self) => {
  return self.indexOf(value) === index
}

/**
 * Provides a set of services for managing products in the admin panel.
 *
 * @param {string} baseUrl - The base URL for the API.
 * @param {string} endpoint - The endpoint for the product services.
 * @returns {Object} An object containing methods for product management.
 * 
 * @property {Function} getProducts - Fetches a list of products based on filters.
 * @param {Object} getProducts.params - Parameters for filtering products.
 * @param {Array} getProducts.params.brands - Selected brands.
 * @param {Array} getProducts.params.brandsAll - All available brands.
 * @param {string} [getProducts.params.title] - Product title or SKU to search for.
 * @param {Array} [getProducts.params.status] - Product statuses to filter by.
 * @param {number} [getProducts.params.limit=20] - Number of products to fetch.
 * @param {string|null} [getProducts.params.cursor=null] - Cursor for pagination.
 * @param {string} [getProducts.params.direction='next'] - Pagination direction.
 * @param {string} [getProducts.params.sortKey='UPDATED_AT'] - Sorting key.
 * @param {boolean} [getProducts.params.reverse=true] - Sorting order.
 * @param {Array} [getProducts.params.category] - Categories to filter by.
 * @param {AbortSignal} [getProducts.params.signal] - Abort signal for the request.
 * @returns {Promise<Object>} The fetched product data.
 * 
 * @property {Function} createProduct - Creates a new product.
 * @param {Object} createProduct.body - The product data to create.
 * @returns {Promise<Object>} The created product data.
 * 
 * @property {Function} updateProduct - Updates an existing product.
 * @param {Object} updateProduct.body - The product data to update.
 * @param {Object} updateProduct.cancelToken - Cancel token for the request.
 * @returns {Promise<Object>} The updated product data.
 * 
 * @property {Function} uploadFiles - Uploads files related to a product.
 * @param {FormData} uploadFiles.data - The files to upload.
 * @returns {Promise<Object>} The response from the upload.
 * 
 * @property {Function} getProductDetailById - Fetches product details by ID.
 * @param {Object} getProductDetailById.params - Parameters for fetching details.
 * @param {string} getProductDetailById.params.id - The product ID.
 * @returns {Promise<Object>} The product details.
 * 
 * @property {Function} deleteProductById - Deletes a product by ID.
 * @param {Object} deleteProductById.params - Parameters for deleting a product.
 * @param {string} deleteProductById.params.id - The product ID.
 * @returns {Promise<Object>} The response from the deletion.
 * 
 * @property {Function} getProductBySku - Fetches products by SKU.
 * @param {Object} getProductBySku.params - Parameters for fetching products.
 * @param {Array} getProductBySku.params.listSku - List of SKUs to fetch.
 * @param {AbortSignal} [getProductBySku.params.signal] - Abort signal for the request.
 * @returns {Promise<Object>} The fetched product data.
 * 
 * @property {Function} exportProductAllData - Exports all product data based on filters.
 * @param {Object} exportProductAllData.params - Parameters for exporting data.
 * @param {Array} exportProductAllData.params.brands - Selected brands.
 * @param {Array} exportProductAllData.params.brandsAll - All available brands.
 * @param {Array} [exportProductAllData.params.status] - Product statuses to filter by.
 * @param {Array} [exportProductAllData.params.category] - Categories to filter by.
 * @param {string} [exportProductAllData.params.title] - Product title to search for.
 * @returns {Promise<Object>} The exported product data.
 * 
 * @property {Function} exportAllById - Exports all product data by bulk ID.
 * @param {string} exportAllById.bulkId - The bulk ID for the export.
 * @returns {Promise<Response>} The response from the export.
 */
const productAdminServices = (baseUrl, endpoint) => ({
  getProducts: async function ({
    brands,
    brandsAll,
    title,
    status,
    limit = 20,
    cursor = null,
    direction = 'next',
    sortKey = 'UPDATED_AT',
    reverse = true,
    category = undefined,
    signal = undefined
  }) {
    let brandList

    if (brands.length === brandsAll.length) {
      brandList = brands
        .map(({ seller }) => `(tag:'${seller.tagSeller}')`)
        .filter(onlyUnique)
        .join(' OR ')
    } else {
      const brandsNotSelected = brandsAll
        .filter(brand =>
          brands.some(br => br.seller.tagSeller === brand.seller.tagSeller)
        )
        .filter(
          brand => !brands.some(br => br.brandName === brand.brandName)
        )
        .map(({ brandName }) => `( NOT vendor:'${brandName}')`)
        .filter(onlyUnique)
        .join(' AND ')
      brandList = `${brands
        .map(({ seller }) => `(tag:'${seller.tagSeller}')`)
        .filter(onlyUnique)
        .join(' OR ')} AND ${brandsNotSelected}`
    }
    const statusList = status
      ? status
          .map(value => `(status:${statusBase.get(value)})`)
          .join(' OR ')
      : undefined
    const categoryList = category
      ? category
          .map(
            product_type_name => `(product_type:'${product_type_name}')`
          )
          .join(' OR ')
      : undefined

    const response = await fetch(`${baseUrl}/${endpoint}/list_mkp`, {
      method: 'POST',
      body: JSON.stringify({
        query: `${brandList}${categoryList ? ` AND ${categoryList}` : ''}${
          title ? ` AND (title:*${title}* OR sku:*${title}*)` : ''
        }${status ? ` AND ${statusList}` : ''}`,
        direction,
        cursor,
        quantity: limit,
        sortKey,
        reverse
      }),
      headers: {
        'Content-Type': 'application/json',
        authorization: persistentStore.getToken(),
        app: process.env.REACT_APP_APPLICATION_NAME
      },
      signal
    })
    const data = await response.json()

    if (isEmpty(data?.products?.edges)) {
      return new Error('Sin productos')
    }

    return data
  },
  createProduct: async body => {
    const response = fetch(`${baseUrl}/${endpoint}/create`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        authorization: persistentStore.getToken(),
        app: process.env.REACT_APP_APPLICATION_NAME
      }
    })
    return (await response).json()
  },
  updateProduct: async (body, cancelToken) => {
    const response = await request(`${baseUrl}/${endpoint}/update`, {
      method: 'post',
      data: JSON.stringify(body),
      cancelToken
    })

    return response.data
  },
  uploadFiles: async data => {
    const response = await fetch(`${baseUrl}/${endpoint}/upload-files`, {
      method: 'POST',
      headers: {
        authorization: persistentStore.getToken(),
        app: process.env.REACT_APP_APPLICATION_NAME
      },
      body: data
    })

    return response.json()
  },
  getProductDetailById: async ({ id }) => {
    const response = fetch(`${baseUrl}/${endpoint}/detail`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: persistentStore.getToken(),
        app: process.env.REACT_APP_APPLICATION_NAME
      },
      body: JSON.stringify({
        id
      })
    })

    return response
  },
  deleteProductById: async ({ id }) => {
    const response = fetch(`${baseUrl}/${endpoint}/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: persistentStore.getToken(),
        app: process.env.REACT_APP_APPLICATION_NAME
      },
      body: JSON.stringify({ id })
    })
    return (await response).json()
  },
  getProductBySku: async ({ listSku, signal = undefined }) => {
    const response = fetch(`${baseUrl}/${endpoint}/sku`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: persistentStore.getToken(),
        app: process.env.REACT_APP_APPLICATION_NAME
      },
      body: JSON.stringify({
        listSku
      }),
      signal
    })

    return response
  },
  exportProductAllData: async ({
    brands,
    brandsAll,
    status,
    category,
    title
  }) => {
    const brandsNotSelected = brandsAll
      .filter(brand =>
        brands?.some(br => br.seller.tagSeller === brand.seller.tagSeller)
      )
      .filter(
        brand => !brands?.some(br => br.brandName === brand.brandName)
      )
      .map(({ brandName }) => `( NOT vendor:'${brandName}')`)
      .filter(onlyUnique)
      .join(' AND ')
    const brandList = `${brands
      .map(({ seller }) => `(tag:'${seller.tagSeller}')`)
      .filter(onlyUnique)
      .join(' OR ')} AND ${brandsNotSelected}`
    const statusList =
      status && status.length
        ? status
            .map(value => `(status:${statusBase.get(value)})`)
            .join(' OR ')
        : null
    const categoryList =
      category && category.length
        ? category
            .map(
              product_type_name => `(product_type:${product_type_name})`
            )
            .join(' OR ')
        : null

    const response = await fetch(`${baseUrl}/${endpoint}/export/all`, {
      method: 'POST',
      body: JSON.stringify({
        query: `${brandList}${categoryList ? ` AND ${categoryList}` : ''}${
          title ? ` AND title:*${title}*` : ''
        }${statusList ? ` AND ${statusList}` : ''}`
      }),
      headers: {
        'Content-Type': 'application/json',
        authorization: persistentStore.getToken(),
        app: process.env.REACT_APP_APPLICATION_NAME
      }
    })

    return response.json()
  },
  exportAllById: async bulkId => {
    const response = await fetch(
      `${baseUrl}/${endpoint}/export/all/${bulkId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type':
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          authorization: persistentStore.getToken(),
          app: process.env.REACT_APP_APPLICATION_NAME
        }
      }
    )
    return response
  }
})

export default productAdminServices(
  process.env.REACT_APP_API_PRODUCT_PATH,
  'product'
)
